<template>
  <div class="research-group-sharing">
    <div class="help-text">
      Sharing a tree with a group allows members of the group to explore the tree together.
      Sharing a tree does not allow anyone to edit it.
    </div>
    <div v-if="!isSharedOrInvitedRef" class="alert alert-primary alert-sm">
      This tree is not shared with any groups.      
    </div>
    <ul class="list-unstyled related-list">
      <li v-for="inv in dataGroupInvitesRef" :key="inv.id">
        <FamilyGroupItem 
          :family-group-id="inv.principalId"
          select-mode 
          :status="PendingStatus"
          @click="onSelectDataGroupInvite(inv)"/>
      </li>
      <li v-for="dm in familyGroupMembersRef" :key="dm.id">
        <FamilyGroupItem :family-group-id="dm.principalId"></FamilyGroupItem>
        <div class="item-actions">
          <button type="button" v-if="canEditSharingRef" class="btn btn-inline btn-link" @click="onRemoveFamilyGroup(dm)">
            Remove
          </button>
        </div>
      </li>
    </ul>
    <div class="actions">
      <template v-if="canEditSharingRef">
        <button type="button" v-if="isSharedOrInvitedRef" class="btn btn-inline btn-link" @click="onShare">
          Share with another group
        </button>
        <button type="button" v-else class="btn btn-primary" @click="onShare">
          <PlusIcon size="1.2x" class="mb-1"/> Share this tree
        </button>
      </template>
    </div>
    <DataGroupShareModal ref="dataGroupShareModalRef" @request-share="onRequestShare"/>
    <DataGroupInviteModal ref="dataGroupInviteModalRef"></DataGroupInviteModal>
    <DataGroupMemberRemoveModal ref="removeFamilyGroupModalRef"></DataGroupMemberRemoveModal>
  </div>
</template>

<style scoped lang="scss">
.research-group-sharing {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.help-text {
  max-width: 600px;
}

h5 {
  margin: 0;
}

.area-options {
  margin-left: 1rem;
}

ul.related-list {
  margin: 0;
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .item-actions {
    margin-left: calc(35px + 0.5rem); // from CompactItem
    font-size: 0.875rem;
  }
}

.actions .btn-link {
  font-size: 0.875rem;
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupInviteStore } from '@/gp/DataGroupInviteStore'
import { ItemPermissions, PrincipalType, InvitationResult, DataGroupMember, DataGroupInvite } from '@/gp/GroupAdminModel'
import { PlusIcon } from '@zhuowenli/vue-feather-icons'
import { LoadMode } from '@/util/AsyncData'
import { isDefined } from '@/util/TypeScriptUtil'
import FamilyGroupItem from '@/manage/familygroups/FamilyGroupItem.vue'
import DataGroupShareModal from '@/manage/datagroups/DataGroupShareModal.vue'
import DataGroupInviteModal from '@/manage/datagroups/DataGroupInviteModal.vue'
import DataGroupMemberRemoveModal from '@/manage/datagroups/DataGroupMemberRemoveModal.vue'
import { PendingStatus } from '@/util/AppUtil'

const props = defineProps({
  dataGroupId: String
})

const showClosedRequestsRef = ref(false)

const dataGroupShareModalRef = ref<InstanceType<typeof DataGroupShareModal>>()
const dataGroupInviteModalRef = ref<InstanceType<typeof DataGroupInviteModal>>()
const removeFamilyGroupModalRef = ref<InstanceType<typeof DataGroupMemberRemoveModal>>()

const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupInviteStore = useDataGroupInviteStore()

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(props.dataGroupId, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const canEditSharingRef = computed(() => (permissionsRef.value & ItemPermissions.ModifyPermissions) > 0)

const familyGroupMembersRef = computed(() => {
  return dataGroupMemberStore.getMembersForDataGroups([props.dataGroupId], LoadMode.EnsureLoaded)
    .filter(m => m.principalType == PrincipalType.FamilyGroup)
})
const dataGroupInvitesRef = computed(() => {
  const inviteIds = dataGroupInviteStore.getAsyncKeyListForDataGroup(props.dataGroupId, LoadMode.EnsureLoaded)?.data?.keys ?? []
  return dataGroupInviteStore.getAsyncInviteList(inviteIds) // loaded with key list
    .map(a => a.data).filter(isDefined)
    .filter(inv => showClosedRequestsRef.value || inv.result == InvitationResult.Pending)
})
const isSharedOrInvitedRef = computed(() => familyGroupMembersRef.value.length || dataGroupInvitesRef.value.length)

function onSelectDataGroupInvite(inv: DataGroupInvite) {
  dataGroupInviteModalRef.value?.openEdit(inv.id!)
}

function onShare() {
  const familyGroupIds = familyGroupMembersRef.value.map(m => m.principalId!)
  familyGroupIds.push(...dataGroupInvitesRef.value.map(inv => inv.principalId!))
  dataGroupShareModalRef.value?.open(props.dataGroupId!, familyGroupIds)
}

function onRequestShare(familyGroupId: string) {
  dataGroupInviteModalRef.value?.openNew(props.dataGroupId!, familyGroupId)
}

function onRemoveFamilyGroup(dm: DataGroupMember) {
  removeFamilyGroupModalRef.value?.open(dm)
}
</script>
